import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiCall, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Send, Paperclip, Mic, MicOff, Image, FileText, Download, X } from "lucide-react";

export default function ChatPage() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const fetchContacts = async () => {
    try {
      const { data } = await apiCall("get", "/chat/contacts");
      setContacts(data.contacts);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchContacts(); }, []);

  const fetchMessages = useCallback(async () => {
    if (!selectedContact) return;
    try {
      const { data } = await apiCall("get", `/chat/messages?with_user=${selectedContact._id}`);
      // Play sound if new messages
      if (data.messages.length > messages.length && messages.length > 0) {
        try { new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgiKWtm3hPOE18mq6jfVQ7S3GUqqJ+VT1Ld5atpIBWPkx3lq2kf1Y+THiXr6aBVz9MeJiwo4FYP0x4mK+jgVhATHiYsKOBWEBMeJivo4FYQE14mLCjgVhATXiYr6OBWEBNeJivo4FYQE14mK+jgVhATQ=").play().catch(() => {}); } catch {}
      }
      setMessages(data.messages);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {}
  }, [selectedContact]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Poll for new messages AND refresh contacts (to clear unread badges)
  useEffect(() => {
    if (!selectedContact) return;
    const i = setInterval(() => { fetchMessages(); fetchContacts(); }, 3000);
    return () => clearInterval(i);
  }, [selectedContact, fetchMessages]);

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedContact) return;
    setSending(true);
    try {
      await apiCall("post", "/chat/send", { to: selectedContact._id, message: newMsg, type: "text" });
      setNewMsg("");
      fetchMessages();
      fetchContacts();
    } catch { toast.error("Failed"); }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("token");
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      const data = await resp.json();
      if (data.path) {
        const msgType = file.type.startsWith("image/") ? "image" : file.type.startsWith("audio/") ? "audio" : "file";
        await apiCall("post", "/chat/send", { to: selectedContact._id, message: file.name, type: msgType, file_path: data.path, file_name: file.name });
        fetchMessages();
        fetchContacts();
        toast.success("File sent");
      }
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice_${Date.now()}.webm`, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", file);
        const token = localStorage.getItem("token");
        try {
          const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/upload`, {
            method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
          });
          const data = await resp.json();
          if (data.path) {
            await apiCall("post", "/chat/send", { to: selectedContact._id, message: "Voice message", type: "audio", file_path: data.path, file_name: file.name });
            fetchMessages();
            toast.success("Voice sent");
          }
        } catch { toast.error("Failed"); }
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const getFileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${localStorage.getItem("token")}`;

  const renderMessage = (msg, i) => {
    const isMe = msg.from_id === (user?._id || user?.id);
    return (
      <div key={i} className={`flex ${isMe ? "justify-end" : "justify-start"} mb-2`}>
        <div className={`max-w-[70%] rounded-lg px-3 py-2 ${isMe ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          {!isMe && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{msg.from_name}</p>}
          {msg.type === "text" && <p className="text-sm break-words">{msg.message}</p>}
          {msg.type === "image" && (
            <div>
              <img src={getFileUrl(msg.file_path)} alt="" className="max-w-full rounded max-h-48 cursor-pointer" onClick={() => window.open(getFileUrl(msg.file_path), "_blank")} />
              <p className="text-xs mt-1 opacity-70">{msg.file_name}</p>
            </div>
          )}
          {msg.type === "audio" && (
            <div>
              <audio controls src={getFileUrl(msg.file_path)} className="max-w-full h-8" />
              <p className="text-xs mt-1 opacity-70">Voice message</p>
            </div>
          )}
          {msg.type === "file" && (
            <a href={getFileUrl(msg.file_path)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80">
              <FileText className="w-4 h-4" /><span className="text-sm underline">{msg.file_name || msg.message}</span><Download className="w-3 h-3" />
            </a>
          )}
          <p className={`text-[10px] mt-1 ${isMe ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{msg.created_at?.split("T")[1]?.slice(0, 5) || ""}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-8rem)] gap-4" data-testid="chat-page">
      {/* Contacts */}
      <Card className={`${selectedContact ? "hidden md:flex" : "flex"} w-full md:w-72 shrink-0 border border-border flex-col`}>
        <div className="p-3 border-b border-border">
          <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Team Chat</h3>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {contacts.map(c => (
              <div key={c._id} onClick={() => setSelectedContact(c)} className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${selectedContact?._id === c._id ? "bg-primary/10" : "hover:bg-muted"}`} data-testid={`chat-contact-${c._id}`}>
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{c.name?.[0]?.toUpperCase()}</div>
                  {c.is_online && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-card" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between"><p className="text-sm font-medium truncate">{c.name}</p>{c.unread > 0 && <Badge className="bg-primary text-primary-foreground text-[10px] h-5 w-5 rounded-full flex items-center justify-center p-0">{c.unread}</Badge>}</div>
                  <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className={`${!selectedContact ? "hidden md:flex" : "flex"} flex-1 border border-border flex-col`}>
        {selectedContact ? (
          <>
            <div className="p-3 border-b border-border flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden" onClick={() => setSelectedContact(null)}><X className="w-4 h-4" /></Button>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{selectedContact.name?.[0]?.toUpperCase()}</div>
              <div><p className="text-sm font-semibold">{selectedContact.name}</p><p className="text-xs text-muted-foreground">{selectedContact.is_online ? "Online" : "Offline"}</p></div>
            </div>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-1">
                {messages.map((m, i) => renderMessage(m, i))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            <div className="p-3 border-t border-border flex items-center gap-2">
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,audio/*,.pdf,.doc,.docx,.txt" />
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => fileInputRef.current?.click()} data-testid="chat-attach-btn"><Paperclip className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className={`h-8 w-8 shrink-0 ${recording ? "text-red-500 animate-pulse" : ""}`} onClick={recording ? stopRecording : startRecording} data-testid="chat-mic-btn">{recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</Button>
              <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} placeholder="Type a message..." className="flex-1" data-testid="chat-input" />
              <Button size="icon" className="h-8 w-8 shrink-0" onClick={sendMessage} disabled={sending || !newMsg.trim()} data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground"><p className="text-sm">Select a contact to start chatting</p></div>
        )}
      </Card>
    </div>
  );
}
